import {
    ChatAltIcon, ExclamationCircleIcon, NinchatSidebarIcon, UsersIcon,
} from '../Icon'
import {
    styledActionContainer, styledActions, styledMenubar,
} from '../Menubar/styles/Menubar'
import Button from 'components/Button'
import CannedMessagesList from 'components/CannedMessagesList'
import ChannelMembers from '../ChannelMembers'
import Highlight from '../Highlight'
import {NinchatCannedMessage} from 'api/types/properties'
import React from 'react'
import UserInfo from '../UserInfo'
import {styled} from '../../stitches/config'

const StyledChannelSidebar = styled('div', {
    backgroundColor: '$channelSidebarBackground',
    borderLeft: '1px solid $channelSidebarBorder',
    display: 'flex',
    flexDirection: 'column',
    overflowX: 'hidden',
    transition: 'width 0.2s; width: 0',
    variants: {
        floating: {
            true: {
                bottom: 0,
                position: 'absolute',
                right: 0,
                top: 0,
                zIndex: '$chatOnJitsi',
            },
        },
        open: {
            true: {
                visibility: 'visible',
                width: '$channelSidebarWidth',
            },
        },
    },
    visibility: 'hidden',
    width: 0,
})

const StyledChannelSidebarMenubar = styled('div', styledMenubar)
const StyledMenuH2 = styled('h2', {
    color: '$menubarHeaderText',
    flexGrow: 1,
    fontSize: '$menubarHeaderText',
    fontWeight: '$menubarHeaderText',
    margin: 0,
})
const StyledActionContainer = styled('div', styledActionContainer)
const StyledActions = styled('div', styledActions)

const StyledChannelSidebarContent = styled('div', {
    '& h2': {
        '& span': {marginLeft: '0.6rem'},
        '& svg': {
            color: '$icon',
            width: '1.5rem',
        },
        'display': 'flex',
        'fontSize': '0.9rem',
        'lineHeight': '1.5rem',
    },
    'boxSizing': 'border-box',
    'display': 'flex',
    'flexDirection': 'column',
    'height': '100%',
    'overflow': 'auto',
    'padding': '$channelSidebarPadding',
    'width': '$channelSidebarWidth',
})

export type ChannelSidebarViewProps = {
    audienceInfoText: string
    cannedMessagesText: string
    channelId?: string
    channelInfoText: string
    channelMembersText: string
    channelTopic: string | null
    channelTopicText: string
    hasCannedMessages: boolean
    hideAudienceInfoText: string
    hideChannelInfoText: string
    isAudienceChannel: boolean
    onSelectCannedMessage: (message: NinchatCannedMessage) => void
    onToggleChannelSidebar: (event?: React.MouseEvent<HTMLButtonElement>) => void
    showCannedMessagesClassicView: boolean
    showChannelSidebar: boolean
    userId?: string
}

const ChannelSidebarView: React.FC<ChannelSidebarViewProps> = ({
    audienceInfoText,
    cannedMessagesText,
    channelId,
    channelInfoText,
    channelMembersText,
    channelTopic,
    channelTopicText,
    hasCannedMessages,
    hideAudienceInfoText,
    hideChannelInfoText,
    isAudienceChannel,
    onSelectCannedMessage,
    onToggleChannelSidebar,
    showCannedMessagesClassicView,
    showChannelSidebar,
    userId,
}) => (
    <StyledChannelSidebar floating={{'@small': true}} open={showChannelSidebar}>
        {!!showChannelSidebar &&
            <>
                <StyledChannelSidebarMenubar>
                    <StyledMenuH2>{isAudienceChannel ? audienceInfoText : channelInfoText}</StyledMenuH2>
                    <StyledActionContainer>
                        <StyledActions>
                            <Button
                                color='text'
                                iconLeft={NinchatSidebarIcon}
                                onPress={() => { onToggleChannelSidebar() }}
                                title={isAudienceChannel ? hideAudienceInfoText : hideChannelInfoText}
                            />
                        </StyledActions>
                    </StyledActionContainer>
                </StyledChannelSidebarMenubar>
                <StyledChannelSidebarContent>
                    {!!userId &&
                    <UserInfo
                        channelId={channelId || ''}
                        userId={userId as string}
                    />}
                    {!!isAudienceChannel && !!showCannedMessagesClassicView && Boolean(hasCannedMessages) && !!onSelectCannedMessage &&
                    <div>
                        <h2><ChatAltIcon /><span>{cannedMessagesText}</span></h2>
                        <CannedMessagesList onSelect={onSelectCannedMessage} />
                    </div>}
                    {!isAudienceChannel && !!channelId &&
                    <div>
                        {!!channelTopic &&
                        <div>
                            <h2><ExclamationCircleIcon /><span>{channelTopicText}</span></h2>
                            <Highlight>{channelTopic}</Highlight>
                        </div>}
                        <h2><UsersIcon /><span>{channelMembersText}</span></h2>
                        <ChannelMembers
                            arrangeInRow={true}
                            channel_id={channelId as string}
                            openSidebar={onToggleChannelSidebar}
                        />
                    </div>}
                </StyledChannelSidebarContent>
            </>}
    </StyledChannelSidebar>
)

ChannelSidebarView.defaultProps = {
    channelId: undefined,
    userId: undefined,
}

const isEqual = (prevProps: ChannelSidebarViewProps, nextProps: ChannelSidebarViewProps) => prevProps?.channelId === nextProps?.channelId &&
        prevProps?.isAudienceChannel === nextProps?.isAudienceChannel &&
        prevProps?.showCannedMessagesClassicView === nextProps?.showCannedMessagesClassicView &&
        prevProps?.showChannelSidebar === nextProps?.showChannelSidebar &&
        prevProps?.userId === nextProps?.userId

export default React.memo(ChannelSidebarView, isEqual)
